import { Card, DatePicker, IconWrapper, Select } from "@caprikorn/dynamo-ui";
import { classNames, getDateString } from "@caprikorn/dynamo-utils";
import { type FC, useId } from "react";
import { Controller, useWatch } from "react-hook-form";

import { CircleCheck, PaperPlane } from "@icons";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./RequestedBenefitCard.module.css";
import type {
  RequestBenefits,
  RequestedBenefitCardProps,
} from "./RequestedBenefitCard.types";
import type { AddPodFormData } from "../AddPodForm/AddPodForm.types";
import { getIsFilled } from "../AddPodForm/AddPodForm.utils";
import formClasses from "../form.module.css";

const REQUEST_BENEFITS: RequestBenefits[] = ["CHF", "PMES", "MES"];

const getMinDate = () => {
  const date = new Date();
  const minimalDelay = 1;
  date.setDate(date.getDate() + minimalDelay);
  return getDateString(date);
};

const requestBenefitFields: Array<keyof AddPodFormData> = [
  "requestedBenefit",
  "requestedDate",
];

export const RequestedBenefitCard: FC<RequestedBenefitCardProps> = ({
  errors,
  control,
}) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const cardId = useId();

  const [requestedBenefit, requestedDate] = useWatch({
    control,
    name: ["requestedBenefit", "requestedDate"],
  });

  const isFilled = getIsFilled(requestBenefitFields, errors, {
    requestedBenefit,
    requestedDate,
  });

  return (
    <Card aria-labelledby={cardId} as="section" className={formClasses.card}>
      <h3
        className={classNames(formClasses.titleContainer, "font-title-md")}
        id={cardId}
      >
        {isFilled ? (
          <IconWrapper
            className={formClasses.successIcon}
            role="presentation"
            size="lg"
            src={CircleCheck}
          />
        ) : (
          <IconWrapper role="presentation" size="md" src={PaperPlane} />
        )}
        {t("addPodDrawer.requestedBenefit.title")}
      </h3>
      <div className={classes.formContainer}>
        <div className={classes.inputContainer}>
          <Controller
            control={control}
            name="requestedBenefit"
            render={({ field, fieldState }) => (
              <Select
                {...field}
                label={t("addPodDrawer.requestedBenefit.fields.benefit.label")}
                size="lg"
                value={field.value === "" ? null : field.value}
              >
                <Select.Trigger
                  className={formClasses.dropdown}
                  data-testid="requestedBenefitSelect"
                  hasError={Boolean(fieldState.error)}
                  hint={
                    fieldState.error?.message &&
                    t(fieldState.error.message, { ns: "common" })
                  }
                />
                <Select.Dropdown className={formClasses.dropdown}>
                  <Select.Options>
                    {REQUEST_BENEFITS.map((option) => (
                      <Select.Option
                        key={option}
                        label={t(
                          `addPodDrawer.requestedBenefit.fields.benefit.${option}`,
                        )}
                        value={option}
                      />
                    ))}
                  </Select.Options>
                </Select.Dropdown>
              </Select>
            )}
            rules={{ required: "form.error.required" }}
          />
        </div>
        <div className={classes.datepickerContainer}>
          <Controller
            control={control}
            name="requestedDate"
            render={({ field, fieldState }) => (
              <DatePicker
                {...field}
                isClearable={false}
                label={t("addPodDrawer.requestedBenefit.fields.requestedDate")}
                minValue={getMinDate()}
                onChange={(dates) => field.onChange(dates[0])}
                value={[field.value]}
              >
                <DatePicker.Trigger
                  className={formClasses.dropdown}
                  hasError={Boolean(fieldState.error)}
                  hint={
                    fieldState.error?.message &&
                    t(fieldState.error.message, { ns: "common" })
                  }
                  size="lg"
                  usePresetLabel={false}
                />
                <DatePicker.Dropdown />
              </DatePicker>
            )}
            rules={{ required: "form.error.required" }}
          />
        </div>
      </div>
    </Card>
  );
};
